import styled from '@emotion/styled';
import { Button, ButtonGroup, pxToRem } from '@modernhealth/vital-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import {
    CareRecommendationModality,
    CareRecommendationNode,
    CareRecommendationRecommendation,
} from 'src/generated/webapp_gql';
import { ModalityLoader } from 'src/modules/onboarding/components/ModalityLoader';
import { useRecommendCareMutation } from 'src/modules/onboarding/documents/recommendCareMutation.generated';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { color, font, mediaQueries, shadow } from 'src/shared/styles/variables';
import onMyOwn from 'src/shared/svg/on-my-own.svg';
import oneOnOne from 'src/shared/svg/one-on-one.svg';
import withASmallGroup from 'src/shared/svg/with-a-small-group.svg';

import { useSelectModalityMutation } from '../documents/selectModalityMutation.generated';
import { PickYourTypeOfCarePageTestID } from './testIds';
import { captureException } from 'src/shared/helpers/captureException';

const Wrapper = styled.div({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: pxToRem(1050),
    minHeight: '100vh',
});

const HeadingText = styled.h2(
    {
        color: color.pickledBluewood,
        textAlign: 'center',
        margin: '30px 0 20px',
    },
    font.black(font.size.extraLarge),
);

const TilesWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 1050,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    marginBottom: 20,
    marginTop: 20,
});

const TileWrapper = styled('div')<{ selected: boolean }>(({ selected }) => ({
    padding: 20,
    marginLeft: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '32.5%',
    border: `2px solid ${selected ? color.royalBlue : color.linkWaterLight}`,
    alignItems: 'center',
    borderRadius: 12,
    cursor: 'pointer',
    '&:hover': {
        boxShadow: shadow.floatingShadow,
    },
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
}));

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    margin: '1rem auto 0',
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    width: '3rem',
    height: '3rem',
    backgroundSize: 'contain',
    backgroundPositionX: 'center',
}));

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
        marginTop: 10,
    },
    font.heavy(font.size.medium),
);

const TileDescriptionText = styled.p(
    {
        color: color.blueBayoux,
        maxWidth: 650,
        minHeight: '3em',
        textAlign: 'center',
    },
    font.book(font.size.medium),
);

export const PickYourTypeOfCarePage: React.FC<RouteComponentProps> = ({ history }) => {
    const [choice, setChoice] = useState<CareRecommendationModality | null>(null);
    const dispatch: Dispatch = useDispatch();
    const flags = useFlags();

    const { t } = useTranslation();

    const [recommendCare] = useRecommendCareMutation();
    const [selectModality] = useSelectModalityMutation();
    const { careRecommendations } = useSelector(getUserInformation);
    const [isLoading, setIsLoading] = useState(false);

    const navigateToNextPage = (
        newCareRecommendation?: Pick<CareRecommendationNode, 'recommendation'>,
    ) => {
        let nextPage = '/dashboard';

        const carePreference =
            newCareRecommendation?.recommendation || CareRecommendationRecommendation.Coaching;

        switch (carePreference) {
            case CareRecommendationRecommendation.Circles:
                nextPage = '/join-a-circle';
                break;
            case CareRecommendationRecommendation.DigitalPrograms:
                nextPage = '/on-my-own';
                break;
            case CareRecommendationRecommendation.Coaching:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-coach';
                }
                break;
            case CareRecommendationRecommendation.MentalHealthCoaching:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-mental-health-coach';
                }
                break;
            case CareRecommendationRecommendation.Therapy:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-therapist';
                }
                break;
            default:
                nextPage = '/dashboard';
        }

        if (flags?.showAlreadyWorkingWithTherapist) {
            history.push('/already-working-with-therapist', { nextPage });
        } else {
            history.push(nextPage, { onboarding: true });
        }
    };

    const handleGetStarted = async (modalityPreference: CareRecommendationModality) => {
        try {
            setIsLoading(true);
            const selectModalityResponse = await selectModality({
                variables: {
                    uuid: careRecommendations[0].uuid,
                    modalityPreference,
                },
            });

            if (!selectModalityResponse.data?.selectModality?.careRecommendation?.uuid) {
                throw new Error('Failed to get care recommendation after selecting modality');
            }

            const response = await recommendCare({
                variables: {
                    uuid: selectModalityResponse.data?.selectModality?.careRecommendation?.uuid,
                },
            });

            const careRecommendation = response.data?.recommendCare?.careRecommendation;

            if (!careRecommendation) {
                throw new Error('Did not receive care recommendation');
            }

            await dispatch.user.fetchUserInfo();

            navigateToNextPage(careRecommendation);
        } catch (error) {
            setIsLoading(false);

            toastNotificationsService.renderErrorToast(config.messages.genericError);

            captureException(error, {
                error_info: 'Error: failed to submit a care recommendation',
            });

            navigateToNextPage();
        }
    };

    const handleNotNow = () => handleGetStarted(CareRecommendationModality.Unspecified);

    const typesOfCare = [
        {
            img: onMyOwn,
            title: t('onboarding:pick_your_type_of_care_page.on_my_own.title'),
            description: t('onboarding:pick_your_type_of_care_page.on_my_own.description'),
            value: CareRecommendationModality.OnMyOwn,
        },
        {
            img: withASmallGroup,
            title: t('onboarding:pick_your_type_of_care_page.with_a_small_group.title'),
            description: t('onboarding:pick_your_type_of_care_page.with_a_small_group.description'),
            value: CareRecommendationModality.WithASmallGroup,
        },
        {
            img: oneOnOne,
            title: t('onboarding:pick_your_type_of_care_page.one_on_one.title'),
            description: t('onboarding:pick_your_type_of_care_page.one_on_one.description'),
            value: CareRecommendationModality.OneOnOne,
        },
    ];

    if (isLoading) {
        return <ModalityLoader />;
    }

    return (
        <Wrapper>
            <HeadingText>{t('onboarding:pick_your_type_of_care_page.title')}</HeadingText>
            <TilesWrapper>
                {typesOfCare.map(item => {
                    return (
                        <TileWrapper
                            key={item.title}
                            onClick={() => {
                                setChoice(item.value);
                            }}
                            selected={item.value === choice}
                            data-testid={PickYourTypeOfCarePageTestID[item.value]}
                        >
                            <StyledIcon iconUrl={item.img} />
                            <TileTitle>{item.title}</TileTitle>
                            <TileDescriptionText>{item.description}</TileDescriptionText>
                        </TileWrapper>
                    );
                })}
            </TilesWrapper>
            <div style={{ marginTop: pxToRem(30), width: pxToRem(500) }}>
                <ButtonGroup>
                    <Button fluid onClick={handleNotNow} size="large" variant="secondary">
                        {t('onboarding:pick_your_type_of_care_page.not_sure')}
                    </Button>
                    <Button
                        disabled={!choice}
                        fluid
                        onClick={() => {
                            if (!choice) return;

                            handleGetStarted(choice);
                        }}
                        size="large"
                        variant="primary"
                    >
                        {t('common.continue')}
                    </Button>
                </ButtonGroup>
            </div>
        </Wrapper>
    );
};
